// @ts-strict-ignore

import React, { useEffect, useState } from 'react'
import { classList } from 'services/styles'
import { makeStyles } from 'tss-react/mui'
import PageContext, { IPageContext } from './context'

export interface IPageProps extends React.HTMLProps<HTMLDivElement> {
  sidebarMode?: string
  sidebar?: any
  children?: any
  classes?: Partial<Record<string, string>>
}

export interface IPageState extends React.HTMLProps<HTMLDivElement> {
  isSidebarDrawerOpen: boolean
}

const useStyles = makeStyles()({
  container: {
    position: 'relative' as const,
    zIndex: '2',
    display: 'flex',
    flexWrap: 'wrap' as const,
    justifyContent: 'center',
    transition: 'width 0.4s ease',
  },
})

export function Page(props: IPageProps) {
  const [isSidebarDrawerOpen, setIsSidebarDrawerOpen] = useState<boolean>(false)

  const { sidebar, sidebarMode, children, className, style, ...restProps } = props

  const { classes } = useStyles()

  const renderChildren = (c, context: IPageContext) => {
    if (typeof c === 'function') {
      return c(context)
    }

    return c
  }

  const handleSetSidebarOpenState = (isOpen) => {
    setIsSidebarDrawerOpen(isOpen)
  }

  const context: IPageContext = {
    sidebar: sidebar,
    sidebarMode: sidebarMode,
    sidebarOpen: isSidebarDrawerOpen,
    setSidebarOpenState: handleSetSidebarOpenState,
  }

  return (
    <div
      className={classList(classes.container, className)}
      style={style}
      {...restProps}
    >
      <PageContext.Provider value={context}>
        {renderChildren(children, context)}
      </PageContext.Provider>
    </div>
  )
}

// Wrapper class over the actual Page component for listening to window resize events.
// This is a workaround, as hooks seem to be bugged with SSR.
function PageComponent(props: IPageProps) {
  const [sidebarMode, setSidebarMode] = useState<string>('drawer')

  const detectSidebarMode = () => {
    const isSmallScreen = typeof window !== 'undefined' && window.innerWidth <= 1200
    const isMediumScreen = typeof window !== 'undefined' && window.innerWidth <= 1600
    let s = 'side'

    if (isSmallScreen) {
      s = 'drawer'
    } else if (isMediumScreen) {
      s = 'column'
    }

    setSidebarMode(s)
  }

  useEffect(() => {
    window.addEventListener('resize', detectSidebarMode)
    detectSidebarMode()

    return () => {
      window.removeEventListener('resize', detectSidebarMode)
    }
  }, [])

  return (
    <Page
      sidebarMode={sidebarMode}
      {...props as any}
    />
  )
}

export default PageComponent
